import { LoadedProps } from '@components/router';
import { defCoursesRoute } from '@course/components/courses-app-router';
import { StudentNotifications } from 'client/courses/components/student-notifications';
import { UserMenu } from 'client/courses/components/nav/user-menu';
import { useImageUrl } from 'client/utils/cdn';
import { rpx } from 'client/lib/rpx-client';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import { URLS } from 'shared/urls';
import { CommunityLayout, loadAipAccessState } from '../mirasee-community/community-layout';
import { formatAccessDate, isToday } from 'client/courses/components/aip-access-date';
import { CommunityHeader } from '../mirasee-community/community-header';
import { IcoAcademicCap, IcoSparkles } from '@components/icons';
import { ProgressBar } from '@components/progress-bar';

async function load() {
  const [courses, aipAccess] = await Promise.all([
    rpx.courses.getMyCourses({}),
    loadAipAccessState(),
  ]);
  return { courses, aipAccess };
}

type LoadedCourse = Awaited<ReturnType<typeof load>>['courses'][0];

function CourseCard({ course }: { course: LoadedCourse }) {
  const src = useImageUrl(course.imagePath);
  const href = URLS.student.course({ course });

  return (
    <a
      href={href}
      class="flex gap-4 p-4 rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all"
    >
      <span class="shrink-0 w-16 h-16 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center">
        {src ? (
          <img src={`${src}?width=200`} class="w-full h-full object-cover" />
        ) : (
          <IcoAcademicCap class="w-6 h-6 text-gray-400" />
        )}
      </span>
      <span class="flex flex-col gap-1 min-w-0">
        <span class="font-medium text-gray-900 truncate">{course.title}</span>
        {course.guide.name && <span class="text-sm text-gray-500">{course.guide.name}</span>}
        {course.progress !== undefined && course.progress > 0 && (
          <ProgressBar
            progress={course.progress}
            showPercent={false}
            barClass="bg-indigo-400"
            bgClass="bg-gray-100"
          />
        )}
      </span>
    </a>
  );
}

function byRecentlyOpened(a: LoadedCourse, b: LoadedCourse) {
  const aOpened = a.lastOpenedAt?.getTime() || 0;
  const bOpened = b.lastOpenedAt?.getTime() || 0;
  if (aOpened !== bOpened) {
    return bOpened - aOpened;
  }
  return (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0);
}

// A cohort buyer whose access has not opened yet. They have paid; the only
// thing to tell them is the date.
function AipPendingCallout({ pendingAccessDate }: { pendingAccessDate: string }) {
  return (
    <section class="mt-8 p-6 rounded-xl border border-gray-200 bg-gray-50 flex items-center gap-4">
      <span class="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-50 text-[#FF6B00]">
        <IcoSparkles class="w-5 h-5" />
      </span>
      <div class="flex-1 min-w-0">
        <h2 class="font-semibold text-gray-900">AI Playground</h2>
        <p class="text-sm text-gray-600 mt-0.5">
          Your access opens {formatAccessDate(pendingAccessDate)}.
          {isToday(pendingAccessDate) && ' Refresh this page in a few minutes.'}
        </p>
      </div>
    </section>
  );
}

function AipCallout({ hadMembership }: { hadMembership: boolean }) {
  const verb = hadMembership ? 'Rejoin' : 'Join';

  return (
    <section class="mt-8 p-6 rounded-xl border border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-center gap-4">
      <span class="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-50 text-[#FF6B00]">
        <IcoSparkles class="w-5 h-5" />
      </span>
      <div class="flex-1 min-w-0">
        <h2 class="font-semibold text-gray-900">AI Playground</h2>
        <p class="text-sm text-gray-600 mt-0.5">
          4 live calls a month, the resource library, and the community.
        </p>
      </div>
      <a
        href="/community/join"
        class="shrink-0 inline-block py-3 px-6 rounded-lg font-semibold no-underline text-center"
        style="background: linear-gradient(135deg, #ff6b00 0%, #ffc600 100%); color: #1a1a1a"
      >
        {verb} AI Playground
      </a>
    </section>
  );
}

function Page({ state }: LoadedProps<typeof load>) {
  const courses = state.courses.filter((c) => !c.isBundle).sort(byRecentlyOpened);
  const { isMember, hadMembership, pendingAccessDate } = state.aipAccess;

  return (
    <CommunityLayout
      selectedItem="courses"
      isAipMember={isMember}
      hadMembership={hadMembership}
      pendingAccessDate={pendingAccessDate}
    >
      <CommunityHeader
        actions={
          <>
            {isMember && (
              <StudentNotifications course={{ id: AIP_COURSE_ID, title: 'AI Playground' }} />
            )}
            <UserMenu />
          </>
        }
      />
      <div class="flex items-center gap-3 mb-8">
        <span class="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-50 text-[#FF6B00]">
          <IcoAcademicCap class="w-5 h-5" />
        </span>
        <h1 class="text-2xl font-semibold text-gray-900">My Courses</h1>
      </div>
      {courses.length > 0 ? (
        <div class="flex flex-col gap-3">
          {courses.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      ) : (
        <p class="text-gray-500 text-center py-12">
          When you register for a course, it will show up here.
        </p>
      )}
      {!isMember &&
        (pendingAccessDate ? (
          <AipPendingCallout pendingAccessDate={pendingAccessDate} />
        ) : (
          <AipCallout hadMembership={hadMembership} />
        ))}
    </CommunityLayout>
  );
}

export const route = defCoursesRoute({
  Page,
  authLevel: 'student',
  load,
});
